import { $$, sliderConfig } from '@utils';
import Swiper from 'swiper';
import { select } from 'd3-selection';
import { geoMercator, geoPath } from 'd3-geo';
import { Navigation, Scrollbar, A11y, FreeMode } from 'swiper/modules';
import removeAccentMarkAndUppercase from '@utils/RemoveAccentMarkAndUppercase';
import dataLayerPushSlider from '@utils/dataLayerPushSlider';

function init() {
  const maps = $$('[data-au-basic-map]');
  if (!maps.length) return;

  maps.forEach((root) => {
    buildSlider(root);
    initDatalayerDistributor(root);
    initMap(root);
  });
}

function updateSliderFades(swiper) {
  const el = swiper.el;

  if (swiper.isLocked) {
    el.classList.remove('au-basic-map__slider--fade-left', 'au-basic-map__slider--fade-right');
    return;
  }

  el.classList.toggle('au-basic-map__slider--fade-left', !swiper.isBeginning);
  el.classList.toggle('au-basic-map__slider--fade-right', !swiper.isEnd);
}

function buildSlider(root) {
  root.querySelectorAll('[data-au-basic-map-slider]').forEach((sliderEl) => {
    const swiper = new Swiper(sliderEl, {
      modules: [Navigation, Scrollbar, A11y, FreeMode],
      watchOverflow: true,
      ...sliderConfig,
      on: {
        init() {
          updateSliderFades(this);
        },
        slideChange() {
          updateSliderFades(this);
        },

        setTranslate() {
          updateSliderFades(this);
        },
        reachBeginning() {
          updateSliderFades(this);
        },
        reachEnd() {
          updateSliderFades(this);
        },
        fromEdge() {
          updateSliderFades(this);
        },

        resize() {
          updateSliderFades(this);
        },
        update() {
          updateSliderFades(this);
        },
        touchEnd(swiper, event) {
          dataLayerPushSlider(swiper, event);
        },
      },
    });

    updateSliderFades(swiper);
  });
}

function initDatalayerDistributor(root) {
  const linkHeader = root.querySelector('[data-au-basic-map] [data-au-datalayer="link"]');
  const slides = root.querySelectorAll('[data-au-basic-map-slide]');
  const titleElement = root.querySelector('[data-au-basic-map-distributor-title]');

  const titleText = titleElement ? titleElement.innerText : '';

  if (linkHeader) {
    linkHeader.addEventListener('click', (e) => {
      const clickableElement = e.target.querySelector(':not([data-au-icon])');
      const clickText = clickableElement ? clickableElement.innerText : '';

      if (!clickText || !titleText) return;

      const item_id = `${removeAccentMarkAndUppercase(titleText)}_${removeAccentMarkAndUppercase(clickText)}`;

      window.dataLayer?.push({
        event: 'navigation',
        content_type: 'clickBoton',
        item_id,
      });

      e.stopPropagation();
    });
  }

  slides.forEach((slide) => {
    const link = slide.querySelector('[data-au-datalayer="card"]');

    link.addEventListener('click', (e) => {
      const clickText = e.target.innerText;
      const item_id = `${removeAccentMarkAndUppercase(titleText)}_${removeAccentMarkAndUppercase(clickText)}`;
      window.dataLayer.push({
        event: 'navigation',
        content_type: 'clickCard',
        item_id,
      });

      e.stopPropagation();
    });
  });
}

async function initMap(root) {
  const mapContainer = root.querySelector('[data-au-basic-map-container]');
  const svgNode = root.querySelector('[data-au-basic-map-svg]');
  if (!mapContainer || !svgNode) return;

  if (typeof root.__auBasicMapCleanup === 'function') {
    root.__auBasicMapCleanup();
  }

  root.__auBasicMapInitialized = true;

  const svg = select(svgNode);

  const CONFIG = {
    margin: { top: 6, right: 2, bottom: 6, left: 0 },
    fracWidthInset: 0.22,
    gapTopInset: -40,
    reservedLeft: 120,
    width: 670,
    height: 550,
  };

  const MIN_INSET_H = 80;
  const markerIcon = mapContainer.dataset.auBasicMapMarkerIcon;

  let markerOverlay = null;
  let markerOverlayRaf = null;

  function ensureMarkerOverlay() {
    if (markerOverlay) return markerOverlay;

    if (getComputedStyle(mapContainer).position === 'static') {
      mapContainer.style.position = 'relative';
    }

    markerOverlay = mapContainer.querySelector('[data-au-basic-map-marker-overlay]');

    if (!markerOverlay) {
      markerOverlay = document.createElement('div');
      markerOverlay.setAttribute('data-au-basic-map-marker-overlay', '');
      markerOverlay.className = 'au-basic-map__marker-overlay';
      mapContainer.appendChild(markerOverlay);
    }

    return markerOverlay;
  }

  function clearMarkerOverlay() {
    const overlay = ensureMarkerOverlay();
    overlay.replaceChildren();
  }

  function renderMarkerOverlay() {
    const overlay = ensureMarkerOverlay();

    if (state.mode !== 'points') {
      overlay.replaceChildren();
      return;
    }

    const overlayRect = overlay.getBoundingClientRect();
    const fragment = document.createDocumentFragment();

    svg.selectAll('g[data-au-basic-map-marker]').each(function () {
      const rect = this.getBoundingClientRect();

      const x = rect.left - overlayRect.left + rect.width / 2;
      const y = rect.top - overlayRect.top + rect.height / 2;

      const icon = document.createElement('span');
      icon.className = 'au-basic-map__marker-overlay-icon material-symbols-rounded';
      icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('data-au-basic-map-marker-overlay-icon', '');
      icon.textContent = markerIcon;
      icon.style.left = `${x}px`;
      icon.style.top = `${y}px`;

      fragment.appendChild(icon);
    });

    overlay.replaceChildren(fragment);
  }

  function scheduleRenderMarkerOverlay() {
    cancelAnimationFrame(markerOverlayRaf);
    markerOverlayRaf = requestAnimationFrame(() => {
      renderMarkerOverlay();
    });
  }

  // Status and index
  let geojsonCache = null;
  let state = { mode: 'filled', path: null, gRest: null, gCan: null };
  let entriesIndex = new Map();
  let currentActiveEl = null;
  let pinned = false;
  let hideTimer = null;
  let tooltip = null;
  let tooltipVisible = false;
  let currentTooltipSlug = null;

  // Helpers
  function slugify(str) {
    return String(str)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/ñ/gi, 'n')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  function esCanarias(name) {
    const s = slugify(name);
    return s.includes('palmas') || s.includes('tenerife');
  }

  function boundsFC(path, features) {
    return path.bounds({ type: 'FeatureCollection', features });
  }

  function buildEntriesIndex() {
    entriesIndex = new Map();
    root.querySelectorAll('[data-au-basic-map-slider] [data-au-naked-card]').forEach((el) => {
      const slug = el.getAttribute('data-au-basic-map-province');
      const name = el.querySelector('.au-naked-card__text')?.textContent?.trim();
      const title = el.querySelector('[data-au-datalayer="card"]')?.textContent?.trim();
      const url = el.querySelector('[data-au-datalayer="card"]').href || null;
      const allUrl = el.getAttribute('data-au-basic-map-all-url');
      if (!entriesIndex.has(slug)) entriesIndex.set(slug, []);
      entriesIndex.get(slug).push({ title, name, url, allUrl });
    });
  }

  function getEntriesFor(slug) {
    return entriesIndex.get(slug) || [];
  }

  function getAllUrlForProvince(slug) {
    const items = entriesIndex.get(slug) || [];
    const allAttr = items.find((it) => it.allUrl)?.allUrl;
    if (allAttr) return allAttr;
    if (items.length === 1) return items[0].url || null;
    return null;
  }

  function selectedSlugs() {
    return new Set(entriesIndex.keys());
  }

  function setActive(el) {
    if (currentActiveEl && currentActiveEl !== el) currentActiveEl.classList.remove('au-active');
    currentActiveEl = el;
    el.classList.add('au-active');
  }

  function clearActive() {
    if (currentActiveEl) currentActiveEl.classList.remove('au-active');
    currentActiveEl = null;
  }

  // Tooltip
  const arrowSvg = '<span class="material-symbols-rounded" aria-hidden="true" data-au-icon>arrow_right_alt</span>';

  function createTooltip() {
    if (tooltip) return tooltip;

    tooltip = select('body')
      .append('div')
      .attr('class', 'au-basic-map__tooltip')
      .attr('data-au-basic-map-tooltip', '')
      .attr('role', 'dialog')
      .attr('aria-hidden', 'true')
      .style('display', 'none')
      .style('position', 'fixed');

    // Global tooltip events
    tooltip
      .on('pointerenter', () => clearTimeout(hideTimer))
      .on('pointerleave', () => hideTooltip(false))
      .on('keydown', function (event) {
        if (event.key !== 'Tab') return;

        const tooltipNode = tooltip.node();
        if (!tooltipNode) return;

        const focusables = Array.from(tooltipNode.querySelectorAll('a[href], button, [tabindex]:not([tabindex="-1"])'));
        if (!focusables.length) return;

        const currentIndex = focusables.indexOf(document.activeElement);
        const lastIndex = focusables.length - 1;
        const firstIndex = 0;

        if (event.shiftKey && currentIndex === firstIndex) {
          event.preventDefault();
          if (currentActiveEl) currentActiveEl.focus();
          return;
        }

        if (!event.shiftKey && currentIndex === lastIndex) {
          const activeTargets = getActiveTargets();
          if (!activeTargets.length) return;

          const currentIndexTarget = activeTargets.indexOf(currentActiveEl);
          const isLastTarget = currentIndexTarget === activeTargets.length - 1;

          if (!isLastTarget && currentIndexTarget !== -1) {
            event.preventDefault();
            pinned = true;
            let nextTarget = activeTargets[currentIndexTarget + 1];

            if (state.mode === 'points') {
              const btn = nextTarget.querySelector('button');
              if (btn) nextTarget = btn;
            }

            if (nextTarget && typeof nextTarget.focus === 'function') {
              nextTarget.focus();
            }
          } else {
            event.preventDefault();

            const fromEl = document.activeElement;
            pinned = false;
            hideTooltip(false);

            setTimeout(() => {
              focusNextInDocument(fromEl);
            }, 0);
          }
        }
      })
      .on('click', function (e) {
        const link = e.target.closest('[data-au-datalayer="link"]');

        const linkText = link.querySelector(':not([data-au-icon])').innerText;
        const item_id = `mapa__${removeAccentMarkAndUppercase(linkText)}`;

        window.dataLayer.push({
          eventName: 'navigation',
          type: 'clickCard',
          item_id,
        });
      });

    return tooltip;
  }

  function focusNextInDocument(fromEl) {
    const selector = 'a[href], button, input, textarea, select, summary, ' + '[tabindex]:not([tabindex="-1"])';

    const all = Array.from($$(selector)).filter((el) => !el.disabled && el.getAttribute('aria-hidden') !== 'true');

    let idx = all.indexOf(fromEl);

    if (idx === -1) {
      idx = all.indexOf(document.activeElement);
    }

    if (idx === -1) return;

    const next = all[idx + 1];
    if (next) {
      next.focus();
    }
  }

  function getActiveTargets() {
    if (state.mode === 'points') {
      return Array.from(svg.selectAll('g[data-au-basic-map-marker]').nodes());
    }

    return Array.from(svg.selectAll('[data-au-basic-map-province][data-au-basic-map-province-active="true"]').nodes());
  }

  function showTooltip(name, slug, fixedX, fixedY, ActiveEl) {
    if (ActiveEl) setActive(ActiveEl);
    const t = createTooltip();
    tooltipVisible = true;
    currentTooltipSlug = slug;
    const entries = getEntriesFor(slug);
    let html = '';

    if (entries.length === 1) {
      const e = entries[0];
      const titleText = e.title || '';
      const nameText = e.name || name;
      const arrowLink = e.url
        ? `<a class="au-link au-basic-map__tooltip-link" href="${e.url}" data-au-datalayer="link"><span>${titleText}</span>${arrowSvg}</a>`
        : '';
      html = `${arrowLink}<p class="t-detail">${nameText}</p>`;
    } else {
      const count = entries.length;
      const allUrl = getAllUrlForProvince(slug);
      const arrowLink = allUrl
        ? `<a class="au-link  au-basic-map__tooltip-link" href="${allUrl}" data-au-datalayer="link"><span>Ver los ${count} episodios</span>${arrowSvg}</a>`
        : '';
      html = `${arrowLink}<p class="t-detail">${name}</p>`;
    }

    t.html(html).style('display', 'flex').attr('aria-hidden', 'false');

    positionTooltipFixed(fixedX, fixedY);
    t.style('opacity', 1);
  }

  function positionTooltipFixed(fixedX, fixedY) {
    const padding = 8,
      offsetY = 14;
    if (!tooltip) return;

    const node = tooltip.node();
    if (!node) return;
    const rect = tooltip.node().getBoundingClientRect();
    let x = fixedX - rect.width / 2;
    let y = fixedY + offsetY;
    x = Math.max(padding, Math.min(x, window.innerWidth - rect.width - padding));
    y = Math.max(padding, Math.min(y, window.innerHeight - rect.height - padding));
    tooltip.style('left', `${x}px`).style('top', `${y}px`);
  }

  function hideTooltip(immediate = false) {
    if (!tooltip) return;
    clearTimeout(hideTimer);
    const doHide = () => {
      if (!tooltip) return;
      tooltip.style('opacity', 0);
      tooltip.remove();
      tooltip = null;
      tooltipVisible = false;
      currentTooltipSlug = null;
      clearActive();
    };
    if (immediate) {
      if (!pinned) doHide();
    } else {
      hideTimer = setTimeout(() => {
        if (pinned) return;
        doHide();
      }, 120);
    }
  }

  // Interactions
  function attachInteractions(sel, getLabel, getSlug) {
    const isTouchLike = () => window.matchMedia('(hover: none), (pointer: coarse)').matches;

    const show = (el, d, { yOffset = 0, pin = false } = {}) => {
      const r = el.getBoundingClientRect();
      pinned = pin;

      showTooltip(getLabel(d), getSlug(d), r.left + r.width / 2, r.top + (yOffset || r.height / 2), el);
    };

    sel
      .on('.au-bascic-map__btn', null)

      .on('pointerenter.au-bascic-map__btn', function (event, d) {
        if (event.pointerType === 'touch' || isTouchLike()) return;
        clearTimeout(hideTimer);
        show(this, d);
      })

      .on('pointerleave.au-bascic-map__btn', function (event) {
        if (event.pointerType === 'touch' || isTouchLike()) return;
        hideTooltip(false);
      })

      .on('focus.au-bascic-map__btn', function (event, d) {
        const slug = getSlug(d);

        if (tooltipVisible && currentTooltipSlug === slug) return;

        show(this, d, { yOffset: 8 });
      })

      .on('blur.au-bascic-map__btn', function (event) {
        const next = event.relatedTarget;
        const tooltipNode = tooltip && tooltip.node();

        if (next && tooltipNode && tooltipNode.contains(next)) {
          return;
        }

        hideTooltip(false);
      })

      // Muy útil en iPhone para SVG
      .on('pointerup.au-bascic-map__btn', function (event, d) {
        if (event.pointerType !== 'touch') return;

        event.preventDefault();
        event.stopPropagation();
        clearTimeout(hideTimer);

        pinned = true;
        show(this, d, { yOffset: 8, pin: true });
      })

      .on('click.au-bascic-map__btn', function (event, d) {
        event.preventDefault();
        event.stopPropagation();
        clearTimeout(hideTimer);

        const slug = getSlug(d);
        const alreadyOpen = tooltipVisible && currentTooltipSlug === slug && pinned;

        if (isTouchLike()) {
          if (!alreadyOpen) {
            show(this, d, { yOffset: 8, pin: true });
          } else {
            pinned = true;
          }
        } else {
          pinned = true;

          if (!tooltipVisible || currentTooltipSlug !== slug) {
            show(this, d, { yOffset: 8, pin: true });
          }
        }

        window.dataLayer?.push({
          eventName: 'navigation',
          type: 'clickBoton',
          item_id: `mapa_${slug}`,
        });
      })

      .on('keydown.au-bascic-map__btn', function (event, d) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          clearTimeout(hideTimer);

          const slug = getSlug(d);

          if (tooltipVisible && currentTooltipSlug === slug) {
            pinned = true;
          } else {
            show(this, d, { yOffset: 8, pin: true });
          }

          const tooltipNode = tooltip && tooltip.node();
          if (tooltipNode) {
            const firstFocusable = tooltipNode.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
          return;
        }

        if (event.key === 'Tab' && !event.shiftKey && tooltipVisible) {
          const tooltipNode = tooltip && tooltip.node();
          if (!tooltipNode) return;

          const firstFocusable = tooltipNode.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');

          if (firstFocusable) {
            event.preventDefault();
            pinned = true;
            firstFocusable.focus();
          }
        }
      });
  }

  function attachMarkerInteractions(markerSel) {
    const showFromMarker = (markerEl, d, { yOffset = 0, pin = false } = {}) => {
      const { rect } = getMarkerContext(markerEl);
      pinned = pin;

      showTooltip(
        d.properties.name,
        slugify(d.properties.name),
        rect.left + rect.width / 2,
        rect.top + (yOffset || rect.height / 2),
        markerEl,
      );
    };

    markerSel
      .on('.au-bascic-map__btn', null)

      .on('pointerenter.au-bascic-map__btn', function (event, d) {
        if (event.pointerType === 'touch') return;
        clearTimeout(hideTimer);
        showFromMarker(this, d);
      })

      .on('pointerleave.au-bascic-map__btn', function (event) {
        if (event.pointerType === 'touch') return;
        hideTooltip(false);
      })

      .on('focus.au-bascic-map__btn', function (event, d) {
        const slug = slugify(d.properties.name);

        if (tooltipVisible && currentTooltipSlug === slug) return;

        showFromMarker(this, d, { yOffset: 8 });
      })

      .on('blur.au-bascic-map__btn', function (event) {
        const next = event.relatedTarget;
        const tooltipNode = tooltip && tooltip.node();

        if (next && tooltipNode && tooltipNode.contains(next)) {
          return;
        }

        hideTooltip(false);
      })

      .on('click.au-bascic-map__btn', function (event, d) {
        event.stopPropagation();
        clearTimeout(hideTimer);
        pinned = true;

        const slug = slugify(d.properties.name);
        window.dataLayer.push({
          eventName: 'navigation',
          type: 'clickBoton',
          item_id: `mapa_${slug}`,
        });
      })

      .on('keydown.au-bascic-map__btn', function (event, d) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          clearTimeout(hideTimer);

          const slug = slugify(d.properties.name);

          if (tooltipVisible && currentTooltipSlug === slug) {
            pinned = true;
          } else {
            showFromMarker(this, d, { yOffset: 8, pin: true });
          }

          const tooltipNode = tooltip && tooltip.node();
          if (tooltipNode) {
            const firstFocusable = tooltipNode.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
          return;
        }

        if (event.key === 'Tab' && !event.shiftKey && tooltipVisible) {
          const tooltipNode = tooltip && tooltip.node();
          if (!tooltipNode) return;

          const firstFocusable = tooltipNode.querySelector('a[href], button, [tabindex]:not([tabindex="-1"])');

          if (firstFocusable) {
            event.preventDefault();
            pinned = true;
            firstFocusable.focus();
          }
        }
      })

      .on('pointerup.au-bascic-map__btn', function (event, d) {
        if (event.pointerType !== 'touch') return;

        event.preventDefault();
        event.stopPropagation();
        clearTimeout(hideTimer);

        pinned = true;
        showFromMarker(this, d, { yOffset: 8, pin: true });
      });
  }

  // Draw provinces
  function drawProvinces(g, features, path) {
    return g
      .selectAll('path[data-au-basic-map-province]')
      .data(features, (d) => slugify(d.properties.name))
      .join('path')
      .attr('class', 'au-basic-map__map-province')
      .attr('d', path)
      .attr('data-au-basic-map-province', (d) => slugify(d.properties.name))
      .attr('aria-label', (d) => d.properties.name)
      .attr('tabindex', '0')
      .attr('data-au-basic-map-province-active', 'false');
  }

  function getMarkerContext(markerEl) {
    return {
      marker: markerEl,
      d: markerEl.__data__,
      rect: markerEl.getBoundingClientRect(),
    };
  }

  function joinMarkers(g, features, path) {
    const mk = g.selectAll('g[data-au-basic-map-marker]').data(features, (d) => slugify(d.properties.name));

    mk.exit().remove();

    const enter = mk
      .enter()
      .append('g')
      .attr('data-au-basic-map-marker', '')
      .attr('class', 'au-basic-map__marker')
      .attr('role', 'button')
      .attr('tabindex', '0')
      .attr('focusable', 'true')
      .attr('aria-label', (d) => `Abrir enlaces de la provincia ${d.properties.name}`);

    enter.append('circle').attr('class', 'au-basic-map__marker-hitarea').attr('r', 20).attr('fill', 'transparent');

    enter.append('circle').attr('class', 'au-basic-map__marker-dot').attr('r', 16);

    const merged = enter.merge(mk).attr('transform', (d) => {
      const [cx, cy] = path.centroid(d);
      return `translate(${cx},${cy})`;
    });

    attachMarkerInteractions(merged);

    return merged;
  }

  function updateActiveFlags() {
    const seleccionadas = selectedSlugs();
    svg.selectAll('[data-au-basic-map-province]').each(function () {
      const slug = this.getAttribute('data-au-basic-map-province');
      const activa = seleccionadas.has(slug);
      this.setAttribute('data-au-basic-map-province-active', activa ? 'true' : 'false');

      if (state.mode === 'filled' && activa) {
        this.setAttribute('tabindex', '0');
        this.setAttribute('role', 'button');
      } else {
        this.setAttribute('tabindex', '-1');
        this.removeAttribute('role');
      }
    });
  }

  function getSvgScale() {
    const node = svg.node();
    const vb = node.viewBox.baseVal;
    if (!vb || !vb.width) return 1;

    const bbox = node.getBoundingClientRect();
    return bbox.width / vb.width;
  }

  function updateMarkerButtonSize() {
    const scale = getSvgScale() || 1;

    const targetPx = 32;
    const hitPx = 38;

    const r = targetPx / 2 / scale;
    const hitR = hitPx / 2 / scale;

    svg.selectAll('.au-basic-map__marker-hitarea').attr('r', hitR);
    svg.selectAll('.au-basic-map__marker-dot').attr('r', r);
  }

  function drawMap(geojson) {
    hideTooltip(true);
    clearActive();
    pinned = false;

    state.mode = mapContainer.dataset.auBasicMapType === 'points' ? 'points' : 'filled';

    const width = CONFIG.width;
    const height = CONFIG.height;
    const canarias = geojson.features.filter((f) => esCanarias(f.properties.name));
    const rest = geojson.features.filter((f) => !esCanarias(f.properties.name));
    const galiciaSlugs = new Set(['la-coruna', 'lugo', 'ourense', 'pontevedra']);
    const galicia = rest.filter((f) => galiciaSlugs.has(slugify(f.properties.name)));

    const projection = geoMercator();
    const path = geoPath(projection);

    const tl = [CONFIG.margin.left + (CONFIG.reservedLeft || 0), CONFIG.margin.top];
    const br = [width - CONFIG.margin.right, height - CONFIG.margin.bottom];

    projection.fitExtent([tl, br], { type: 'FeatureCollection', features: rest });

    svg.selectAll('*').remove();
    const gRest = svg.append('g').attr('id', 'rest');
    const gCan = svg.append('g').attr('id', 'canarias-inset');
    const pathFinal = geoPath(projection);

    drawProvinces(gRest, rest, pathFinal);
    drawProvinces(gCan, canarias, pathFinal);

    // Inset Canarias under Galicia
    const [[R0x, R0y], [R1x, R1y]] = boundsFC(pathFinal, rest);

    let G1x;
    if (galicia.length) {
      [[,], [G1x]] = boundsFC(pathFinal, galicia);
    } else {
      G1x = R0x + (R1x - R0x) * 0.18;
    }

    const [[C0x, C0y], [C1x, C1y]] = boundsFC(pathFinal, canarias);

    const restW = R1x - R0x,
      canW = C1x - C0x,
      canH = C1y - C0y;
    const insetW = restW * CONFIG.fracWidthInset;
    let scaleInset = insetW / canW;
    let insetH = canH * scaleInset;

    const availableDown = Math.max(0, height - (R1y + CONFIG.gapTopInset) - CONFIG.margin.bottom);
    if (insetH > availableDown && availableDown > 0) {
      scaleInset *= availableDown / insetH;
      insetH = canH * scaleInset;
    }
    if (insetH < MIN_INSET_H) {
      scaleInset *= MIN_INSET_H / insetH;
      insetH = MIN_INSET_H;
    }

    let top = R1y + CONFIG.gapTopInset;
    if (top + insetH > height - CONFIG.margin.bottom) top = height - CONFIG.margin.bottom - insetH;
    const anchorX = G1x;
    let left = anchorX - canW * scaleInset;
    left = Math.max(CONFIG.margin.left, Math.min(left, width - CONFIG.margin.right - canW * scaleInset));
    const tx = left - scaleInset * C0x,
      ty = top - scaleInset * C0y;
    gCan.attr('transform', `translate(${tx}, ${ty}) scale(${scaleInset})`);

    const bottomRest = R1y;
    const bottomCan = top + insetH;
    const topRest = R0y;
    const topCan = top;
    const topAll = Math.min(topRest, topCan);
    const bottomAll = Math.max(bottomRest, bottomCan);
    const vbHeight = bottomAll - topAll;

    // Crop the viewBox to the useful content
    svg.attr('viewBox', `0 ${topAll} ${width} ${vbHeight}`);

    // Adjust the actual height of the SVG (and maintain the aspect ratio)
    svg.attr('height', vbHeight);

    state = { ...state, path, gRest, gCan };

    updateActiveFlags();
    if (state.mode === 'filled') {
      clearMarkerOverlay();

      const activas = svg.selectAll('[data-au-basic-map-province][data-au-basic-map-province-active="true"]');
      attachInteractions(
        activas,
        (d) => d.properties.name,
        (d) => slugify(d.properties.name),
      );
    }

    if (state.mode === 'points') {
      const sel = selectedSlugs();

      joinMarkers(
        gRest.append('g').attr('class', 'au-basic-map__markers-resto'),
        rest.filter((f) => sel.has(slugify(f.properties.name))),
        path,
      );

      joinMarkers(
        gCan.append('g').attr('class', 'au-basic-map__markers-can'),
        canarias.filter((f) => sel.has(slugify(f.properties.name))),
        path,
      );

      updateMarkerButtonSize();
      scheduleRenderMarkerOverlay();

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          scheduleRenderMarkerOverlay();
        });
      }
    }
  }

  buildEntriesIndex();
  const geoJsonUrl = new URL('./spain-provinces.json', import.meta.url);
  const res = await fetch(geoJsonUrl);
  if (!res.ok) {
    console.error('Error cargando spain-provinces.json');
    return;
  }
  const spainProvinces = await res.json();

  geojsonCache = spainProvinces;
  drawMap(geojsonCache);

  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(() => {
      if (state.mode === 'points') {
        updateMarkerButtonSize();
        scheduleRenderMarkerOverlay();
      }
    });

    ro.observe(mapContainer);
    ro.observe(svgNode);
  }

  // Global events
  let roRaf = null;

  if ('ResizeObserver' in window) {
    if (root.__auBasicMapResizeObserver) {
      root.__auBasicMapResizeObserver.disconnect();
    }

    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(roRaf);
      roRaf = requestAnimationFrame(() => {
        if (state.mode === 'points') {
          updateMarkerButtonSize();
          scheduleRenderMarkerOverlay();
        }
      });
    });

    ro.observe(mapContainer);
    ro.observe(svgNode);

    root.__auBasicMapResizeObserver = ro;
  }

  const onPointerDown = (ev) => {
    if (!pinned || !tooltip) return;
    const nodeTooltip = tooltip.node();
    if (!nodeTooltip) return;

    const isInsideTooltip = nodeTooltip.contains(ev.target);
    const isMarker = ev.target.closest && ev.target.closest('[data-au-basic-map-marker]');
    const isProvPath =
      ev.target.closest && ev.target.closest('[data-au-basic-map-province][data-au-basic-map-province-active="true"]');

    if (!isInsideTooltip && !isMarker && !isProvPath) {
      pinned = false;
      hideTooltip(true);
    }
  };

  const onKeyDown = (ev) => {
    if (ev.key === 'Escape' && pinned) {
      pinned = false;
      hideTooltip(true);
    }
  };

  const onResize = () => {
    if (state.mode === 'points') {
      updateMarkerButtonSize();
      scheduleRenderMarkerOverlay();
    }
  };

  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('keydown', onKeyDown);
  window.addEventListener('resize', onResize);

  root.__auBasicMapCleanup = () => {
    document.removeEventListener('pointerdown', onPointerDown);
    document.removeEventListener('keydown', onKeyDown);
    window.removeEventListener('resize', onResize);

    if (root.__auBasicMapResizeObserver) {
      root.__auBasicMapResizeObserver.disconnect();
      root.__auBasicMapResizeObserver = null;
    }

    cancelAnimationFrame(markerOverlayRaf);
    cancelAnimationFrame(roRaf);

    if (tooltip) {
      tooltip.remove();
      tooltip = null;
    }

    const overlay = mapContainer.querySelector('[data-au-basic-map-marker-overlay]');
    if (overlay) overlay.remove();

    root.__auBasicMapCleanup = null;
    root.__auBasicMapInitialized = false;
  };
}

export default { init };
